import React, { useCallback } from 'react'
import { useLocation } from 'react-router'
import { Link, matchPath } from 'react-router-dom'
import { useSelector } from 'react-redux'

import styled, { css } from 'styled-components'

import { colours } from '@qflow/theme'
import * as ROUTES from 'portal/routes'
import {
  Spacer,
  Flex,
  icons,
  Clickable,
  SideBar,
  ControlPanel
} from 'portal/lib/primitives'

import { useMixpanel } from 'portal/utils/mixpanel'

import { AnnouncementLink } from './AnnouncementLink'
import { LoggedInControl } from './LoggedInControl'
import { LoginButton } from './LoginButton'
import { HelpLink } from './HelpLink'
import { ProjectSwitcher } from './ProjectSwitcher'
import { SupportLink } from './SupportLink'
import { TakeATour } from './TakeATour'
import { useResponsive } from 'portal/lib/hooks'

const EV_NAVIGATION_LOGO_CLICK = 'frontend_navigation_logo_click'

export function Toolbar() {
  const { track } = useMixpanel()
  const { pathname } = useLocation()
  const trackLogoClick = useCallback(() => {
    track(EV_NAVIGATION_LOGO_CLICK, { from_path: pathname })
  }, [pathname, track])
  const isAuthenticated = useSelector(state => !!state.auth.userId)
  const isSideBarCollapsed = useSelector(
    state => state.ui.data.sidebarCollapsed
  )

  const { isMobile } = useResponsive()

  const SideBarState = SideBar.useContext()
  const ControlPanelState = ControlPanel.useContext()

  const sidebarEnabled = isAuthenticated && !ControlPanelState.mobileShow
  const controlPanelEnabled =
    ControlPanelState.isOnPage && !SideBarState.mobileShow

  const invertedLogoPaths = [
    '/',
    ROUTES.SIGNIN_REDIRECT,
    ROUTES.ACCOUNTS,
    ROUTES.DELIVERY_RECORD_EDIT,
    ROUTES.DELIVERY_RECORD_LINE_ITEM_EDIT,
    ROUTES.WASTE_RECORD_EDIT,
    ROUTES.WASTE_RECORD_LINE_ITEM_EDIT
  ]
  const invertLogo = invertedLogoPaths.some(path => matchPath(path, pathname))

  const beamerFeatureFlag =
    window.location.hostname === 'app.qualisflow.com' ||
    window.location.search === '?ff-beamer'

  return (
    <Container isMobile={isMobile}>
      {isMobile ? (
        <>
          <SideBarButton
            onClick={() => SideBarState.setMobileShow(show => !show)}
            active={SideBarState.mobileShow}
            disabled={!sidebarEnabled}
          >
            <icons.Menu />
          </SideBarButton>

          <Flex.Row amount={1} css={FlexFillCSS} horizontalCentre>
            <MobileProjectSwitcher />
          </Flex.Row>

          <SideBarButton
            onClick={() => ControlPanelState.setMobileShow(show => !show)}
            active={ControlPanelState.mobileShow}
            disabled={!controlPanelEnabled}
          >
            <icons.Settings />
          </SideBarButton>
        </>
      ) : (
        <>
          <LogoButton
            id="homeBtn"
            to={'/accounts'}
            onClick={trackLogoClick}
            $invertLogo={invertLogo}
            $isSideBarCollapsed={isSideBarCollapsed}
          >
            {!isSideBarCollapsed ? (
              <LogoIcon qColour={invertLogo ? colours.TEAL : 'currentColor'} />
            ) : (
              <QIcon />
            )}
          </LogoButton>

          <Spacer.Small />

          <Flex.Row amount={1} css={FlexFillCSS}>
            <ProjectSwitcher />
          </Flex.Row>
          {isAuthenticated && (
            <Flex.Row
              amount={0}
              css={FlexFillCSS}
              style={{ marginRight: '10px' }}
            >
              <TakeATour />
            </Flex.Row>
          )}
          {isAuthenticated && beamerFeatureFlag && (
            <Flex.Row
              amount={0}
              css={FlexFillCSS}
              style={{ marginRight: '10px' }}
            >
              <AnnouncementLink />
            </Flex.Row>
          )}
          <Flex.Row
            amount={0}
            css={FlexFillCSS}
            style={{ marginRight: '10px' }}
          >
            {isAuthenticated && <SupportLink />}
          </Flex.Row>

          <Flex.Row amount={0} css={FlexFillCSS}>
            {isAuthenticated && <HelpLink />}
          </Flex.Row>
          <Spacer.Fine />

          <Flex.Row amount={0} css={FlexFillCSS}>
            {isAuthenticated ? <LoggedInControl /> : <LoginButton />}
          </Flex.Row>

          <Spacer.Small />
        </>
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  min-height: 4rem;
  max-height: 4rem;
  z-index: 1000;

  ${({ isMobile }) =>
    isMobile &&
    css`
      min-height: 3rem;
      max-height: 3rem;
    `}

  background-color: ${colours.WHITE};
  box-shadow: 0px 1px 3px 0px #0a294026;

  color: ${colours.BLUEY_GREY};

  overflow: visible;
`

const LogoButton = styled(Link)`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  height: 4rem;
  min-width: ${({ $isSideBarCollapsed }) =>
    $isSideBarCollapsed ? '3.625rem' : '16rem'};
  max-width: ${({ $isSideBarCollapsed }) =>
    $isSideBarCollapsed ? '3.625rem' : '16rem'};

  border: none;
  outline: none;
  cursor: pointer;

  color: ${({ $invertLogo }) =>
    $invertLogo ? colours.CORE_NAVY : colours.WHITE};
  background-color: ${({ $invertLogo }) =>
    $invertLogo ? colours.WHITE : colours.CORE_NAVY};

  &:hover {
    color: ${({ $invertLogo }) =>
      $invertLogo ? colours.CORE_NAVY : colours.WHITE};
  }
`

const LogoIcon = styled(icons.QflowLogo).attrs(() => ({
  style: {}
}))`
  margin-left: 18px;
  display: block;
`
const QIcon = styled(icons.QLogo).attrs(() => ({
  style: {}
}))`
  margin-left: 18px;
  display: block;
`

const FlexFillCSS = css`
  align-self: stretch;
`

const SideBarButton = styled(Clickable)`
  width: 3rem;

  ${({ active, theme }) =>
    active &&
    css`
      :hover:enabled {
        background-color: ${theme.primary.highlightHover};
      }

      :active:enabled {
        background-color: ${theme.primary.highlightHover};
      }

      background-color: ${theme.primary.highlightHover};
    `}

  height: 100%;
  height: -webkit-fill-available;

  :disabled {
    opacity: 0.4;
  }

  border-radius: 0;
`

const MobileBarNavSegment = css`
  flex: 1 1 0;
  align-items: stretch;
`

const MobileProjectSwitcher = styled(ProjectSwitcher)`
  ${MobileBarNavSegment}

  justify-content: flex-end;
  max-width: 10rem;
`
